import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import {
  AlertConfigResponse,
  AlertConfigUpdateRequest,
} from 'src/types/AlertManagementTypes';
import {
  ApiEventsQueryRequestBody,
  ApiEventsQueryResponse,
} from 'src/types/ApiEventTypes';
import {
  ErrorEventCountResponse,
  ErrorEventsOverviewResponse,
  ErrorFilterValuesResponse,
  ErrorQueryRequestBody,
  ErrorQueryResponse,
} from 'src/types/ErrorEventTypes';

export const errorEventsApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    getAlertConfig: builder.query<AlertConfigResponse, void>({
      keepUnusedDataFor: 0,
      providesTags: ['AlertConfig'],
      query: () => SOLVE_V2_BASE_URL + '/error-events/alert-config',
    }),
    getApiEvents: builder.query<
      ApiEventsQueryResponse,
      ApiEventsQueryRequestBody
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      keepUnusedDataFor: 0,
      merge: (currentCache, newItems) => {
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.api_events = [...newItems.api_events];
        } else {
          currentCache.api_events = [
            ...currentCache.api_events,
            ...newItems.api_events,
          ];
        }
      },
      query: ({ conversation_id, page, page_size, sort_direction, url }) => {
        const qParams = new URLSearchParams({
          conversation_id,
          page: page.toString(),
          page_size: page_size ?? '100',
          sort_direction,
          url,
        });

        return (
          SOLVE_V2_BASE_URL +
          '/workflow-builder/intent-conversations/api-events' +
          '?' +
          qParams
        );
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Cache key includes every filter so different lookups don't share one
        // entry. Only `page` is dropped, so pages of the same filter still merge.
        const keyArgs = { ...queryArgs, page: undefined };
        return endpointName + '&' + JSON.stringify(keyArgs);
      },
    }),
    getErrorEventCount: builder.query<
      ErrorEventCountResponse,
      { show_muted?: string } | void
    >({
      providesTags: ['ErrorEvents'],
      query: arg => {
        const showMuted =
          (arg as { show_muted?: string })?.show_muted ?? 'false';
        const qParams = new URLSearchParams({ show_muted: showMuted });
        return SOLVE_V2_BASE_URL + '/error-events/count?' + qParams;
      },
    }),
    // No providesTags — after alert management saves, refetch is handled manually
    // via onSaveSuccess in ErrorPage to ensure page resets to 1 before refetching
    // (required by the infinite scroll merge logic).
    getErrorEvents: builder.query<ErrorQueryResponse, ErrorQueryRequestBody>({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      keepUnusedDataFor: 0,
      merge: (currentCache, newItems) => {
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.error_events = [...newItems.error_events];
        } else {
          currentCache.error_events = [
            ...currentCache.error_events,
            ...newItems.error_events,
          ];
        }
      },
      query: ({
        channel,
        conversation_id,
        error_id,
        error_message,
        error_payload,
        page,
        page_size,
        product,
        severity,
        show_muted,
        sort_column,
        sort_direction,
      }) => {
        const qParams = new URLSearchParams({
          channel,
          conversation_id,
          error_id,
          error_message,
          error_payload,
          page: page.toString(),
          page_size: page_size ?? '100',
          product,
          severity,
          show_muted: show_muted ?? 'false',
          sort_column,
          sort_direction,
        });

        return (
          SOLVE_V2_BASE_URL +
          '/workflow-builder/intent-conversations/error-events' +
          '?' +
          qParams
        );
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Cache key includes every filter so different lookups don't share one entry.
        // Only `page` is dropped, so pages of the same filter still merge.
        const keyArgs = { ...queryArgs, page: undefined };
        return endpointName + '&' + JSON.stringify(keyArgs);
      },
    }),

    getErrorEventsOverview: builder.query<
      ErrorEventsOverviewResponse,
      { show_muted?: string } | void
    >({
      providesTags: ['ErrorEvents'],
      query: arg => {
        const showMuted =
          (arg as { show_muted?: string })?.show_muted ?? 'false';
        const qParams = new URLSearchParams({ show_muted: showMuted });
        return SOLVE_V2_BASE_URL + '/error-events/overview?' + qParams;
      },
    }),
    getErrorFilterValues: builder.query<
      ErrorFilterValuesResponse,
      { show_muted?: string } | void
    >({
      providesTags: ['ErrorEvents'],
      query: arg => {
        const showMuted =
          (arg as { show_muted?: string })?.show_muted ?? 'false';
        const qParams = new URLSearchParams({ show_muted: showMuted });
        return (
          SOLVE_V2_BASE_URL +
          '/workflow-builder/intent-conversations/error-filter-values?' +
          qParams
        );
      },
    }),
    updateAlertConfig: builder.mutation<
      AlertConfigResponse,
      AlertConfigUpdateRequest
    >({
      invalidatesTags: ['AlertConfig', 'ErrorEvents'],
      query: body => ({
        body,
        method: 'PUT',
        url: SOLVE_V2_BASE_URL + '/error-events/alert-config',
      }),
    }),
  }),
});

export const {
  useGetAlertConfigQuery,
  useGetApiEventsQuery,
  useGetErrorEventCountQuery,
  useGetErrorEventsOverviewQuery,
  useGetErrorEventsQuery,
  useGetErrorFilterValuesQuery,
  useUpdateAlertConfigMutation,
} = errorEventsApi;
